import React, { useCallback, useEffect, useRef, useState } from "react";
import Header from "../../commons/Header/Header";
import Sidebar from "../../commons/Sidebar/Sidebar";
import Modal from "../../commons/Modal/Modal";
import { handleApiCall } from "../../apis";
import { endpoints } from "../../utils/endpoints";
import { toast } from "react-toastify";
import {
  getAdminDisplayName,
  getAdminRoleLabel,
  getStoredAdmin,
  getStoredStripeAccountDetail,
  storeStripeAccountDetail,
  clearStripeAccountDetail,
} from "../../utils/adminAuth";
import { UI_CONSTANTS } from "../../utils/constants";

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getYesNoBadge = (value) =>
  value ? (
    <span className="badge badge-success">Yes</span>
  ) : (
    <span className="badge badge-secondary">No</span>
  );

const extractStripeAccountDetail = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  if (
    payload.admin_stripe_account_detail &&
    typeof payload.admin_stripe_account_detail === "object"
  ) {
    return payload.admin_stripe_account_detail;
  }
  if (payload.stripe_account_id || payload._id) {
    return payload;
  }
  return null;
};

function AdminProfile() {
  const [admin, setAdmin] = useState(() => getStoredAdmin());
  const [stripeAccount, setStripeAccount] = useState(() =>
    getStoredStripeAccountDetail()
  );
  const [loadingStripe, setLoadingStripe] = useState(false);
  const [creatingStripe, setCreatingStripe] = useState(false);
  const [refreshingStripe, setRefreshingStripe] = useState(false);
  const [confirmingSuccess, setConfirmingSuccess] = useState(false);
  const [successModal, setSuccessModal] = useState({
    open: false,
    accountDetailId: null,
  });
  const [refreshModal, setRefreshModal] = useState({
    open: false,
    title: "Stripe Refresh Result",
    message: "",
    data: null,
  });
  const stripeWindowRef = useRef(null);

  const applyStripeAccountDetail = (detail) => {
    if (detail?._id) {
      storeStripeAccountDetail(detail);
      setStripeAccount(detail);
      return;
    }
    clearStripeAccountDetail();
    setStripeAccount(null);
  };

  const resolveAccountDetailId = useCallback(() => {
    const storedDetail = getStoredStripeAccountDetail();
    const adminData = getStoredAdmin();
    return (
      storedDetail?._id ||
      adminData?.admin_stripe_account_detail?._id ||
      adminData?.admin_stripe_account_detail_id ||
      adminData?.admin_account_detail_id ||
      null
    );
  }, []);

  const fetchStripeAccountDetail = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoadingStripe(true);
    try {
      const response = await handleApiCall(
        "get",
        endpoints.getAdminStripeAccountDetail
      );

      if (
        response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS ||
        response?.data?.code === 200 ||
        response?.data?.success
      ) {
        const detail = extractStripeAccountDetail(response?.data?.data);
        if (detail?._id) {
          applyStripeAccountDetail(detail);
          return true;
        }
      }

      clearStripeAccountDetail();
      setStripeAccount(null);
      return false;
    } catch (error) {
      console.error("Error fetching admin stripe account detail:", error);
      clearStripeAccountDetail();
      setStripeAccount(null);
      return false;
    } finally {
      if (!silent) setLoadingStripe(false);
    }
  }, []);

  const refreshStripeAccount = useCallback(async () => {
    const accountDetailId = resolveAccountDetailId();

    if (!accountDetailId) {
      toast.error("Stripe account detail not found");
      return { ok: false };
    }

    try {
      const response = await handleApiCall(
        "get",
        `${endpoints.adminAccountCreateRefresh}?admin_account_detail_id=${accountDetailId}`
      );

      if (
        response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS ||
        response?.data?.code === 200 ||
        response?.data?.success
      ) {
        const payload = response?.data?.data || {};
        const detail = extractStripeAccountDetail(payload);

        if (detail?._id) {
          applyStripeAccountDetail(detail);
        }

        return {
          ok: true,
          message:
            response?.data?.message || "Stripe account details refreshed",
          data: payload,
        };
      }

      return { ok: false };
    } catch (error) {
      console.error("Error refreshing admin stripe account:", error);
      return { ok: false };
    }
  }, [resolveAccountDetailId]);

  useEffect(() => {
    setAdmin(getStoredAdmin());
    fetchStripeAccountDetail();
  }, [fetchStripeAccountDetail]);

  const handleCreateStripeAccount = async () => {
    setCreatingStripe(true);
    try {
      const response = await handleApiCall(
        "post",
        endpoints.createAdminStripeAccount
      );

      if (
        response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS ||
        response?.data?.code === 200 ||
        response?.data?.success
      ) {
        const payload = response?.data?.data || {};
        const accountDetail = extractStripeAccountDetail(payload) || {};
        const redirectUrl =
          payload?.onboarding_link?.url ||
          payload?.url ||
          payload?.account_link?.url ||
          payload?.onboarding_url ||
          payload?.stripe_url ||
          null;

        if (accountDetail?._id) {
          applyStripeAccountDetail(accountDetail);
        }

        const isOnboarded =
          accountDetail?.stripe_onboarded === true ||
          accountDetail?.payout_enabled === true;

        if (redirectUrl && typeof redirectUrl === "string" && !isOnboarded) {
          setRefreshModal({
            open: true,
            title: "Stripe Account Created",
            message:
              response?.data?.message ||
              "Stripe onboarding link created successfully",
            data: payload,
          });
          return;
        }

        if (isOnboarded || accountDetail?._id) {
          setSuccessModal({
            open: true,
            accountDetailId: accountDetail?._id || null,
          });
          return;
        }

        toast.error("Onboarding link not found");
      }
    } catch (error) {
      console.error("Error creating admin stripe account:", error);
    } finally {
      setCreatingStripe(false);
    }
  };

  const handleRefreshStripeAccount = async () => {
    setRefreshingStripe(true);
    try {
      const result = await refreshStripeAccount();
      if (result?.ok) {
        setRefreshModal({
          open: true,
          title: "Stripe Refresh Result",
          message: result.message || "Stripe account details refreshed",
          data: result.data || null,
        });
      } else {
        toast.error("Unable to refresh Stripe account details");
      }
    } finally {
      setRefreshingStripe(false);
    }
  };

  const handleCloseRefreshModal = () => {
    setRefreshModal({
      open: false,
      title: "Stripe Refresh Result",
      message: "",
      data: null,
    });
  };

  const formatUnixDateTime = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    // Stripe timestamps are seconds
    return formatDateTime(num > 1e12 ? num : num * 1000);
  };

  const refreshOnboardingLink =
    refreshModal.data?.onboarding_link ||
    refreshModal.data?.account_link ||
    (refreshModal.data?.url
      ? {
          object: refreshModal.data?.object || "account_link",
          created: refreshModal.data?.created,
          expires_at: refreshModal.data?.expires_at,
          url: refreshModal.data.url,
        }
      : null);
  const refreshAccountDetail = extractStripeAccountDetail(refreshModal.data);
  const handleSuccessOk = async () => {
    const accountDetailId = successModal.accountDetailId;

    if (stripeWindowRef.current && !stripeWindowRef.current.closed) {
      try {
        stripeWindowRef.current.close();
      } catch (error) {
        // Ignore cross-window close errors
      }
      stripeWindowRef.current = null;
    }

    if (!accountDetailId) {
      setSuccessModal({ open: false, accountDetailId: null });
      return;
    }

    setConfirmingSuccess(true);
    try {
      const response = await handleApiCall(
        "get",
        `${endpoints.adminAccountCreateSuccess}?admin_account_detail_id=${accountDetailId}`
      );

      if (
        response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS ||
        response?.data?.code === 200 ||
        response?.data?.success
      ) {
        toast.success(
          response?.data?.message || "Stripe account setup completed successfully"
        );
        const detail = extractStripeAccountDetail(response?.data?.data);
        if (detail?._id) {
          applyStripeAccountDetail(detail);
        } else {
          await fetchStripeAccountDetail({ silent: true });
        }
      }
    } catch (error) {
      console.error("Error confirming admin stripe account success:", error);
    } finally {
      setConfirmingSuccess(false);
      setSuccessModal({ open: false, accountDetailId: null });
    }
  };

  const name = getAdminDisplayName(admin);
  const email = admin?.email || "-";
  const role = getAdminRoleLabel(admin);
  const hasStripeAccount = Boolean(stripeAccount?._id);
  const isStripeFullySetup =
    stripeAccount?.stripe_onboarded === true &&
    stripeAccount?.payout_enabled === true;
  const isCreateDisabled = creatingStripe || isStripeFullySetup;

  return (
    <div className="container-scroller">
      <Header />
      <div className="container-fluid page-body-wrapper">
        <Sidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="heading mb-4 d-flex align-items-center">
              <h3 className="text-default weight-400 my-0">Profile</h3>
            </div>

            <div className="border rounded-14 p-4">
              <div className="mb-3">
                <h4 className="text-white mb-1">{name}</h4>
                <p className="text-muted mb-1">{email}</p>
                <span className="badge badge-info">{role}</span>
              </div>

              <div className="row mt-4">
                <div className="col-md-4 mb-3">
                  <div className="border rounded-14 p-3 h-100">
                    <small className="text-muted d-block mb-1">Name</small>
                    <strong className="text-white">{name}</strong>
                  </div>
                </div>
                <div className="col-md-4 mb-3">
                  <div className="border rounded-14 p-3 h-100">
                    <small className="text-muted d-block mb-1">Email</small>
                    <strong className="text-white">{email}</strong>
                  </div>
                </div>
                <div className="col-md-4 mb-3">
                  <div className="border rounded-14 p-3 h-100">
                    <small className="text-muted d-block mb-1">Role</small>
                    <strong className="text-white">{role}</strong>
                  </div>
                </div>
              </div>

              <hr className="border-secondary my-4" />

              <div className="d-flex align-items-center mb-3 flex-wrap">
                <h5 className="text-white mb-0">Stripe Account</h5>
                <div className="ml-auto d-flex flex-wrap align-items-center">
                  <button
                    type="button"
                    className="btn btn-primary rounded-lg px-3 mr-2"
                    onClick={handleCreateStripeAccount}
                    disabled={isCreateDisabled || loadingStripe}
                    title={
                      isStripeFullySetup
                        ? "Stripe account is already onboarded and payout enabled"
                        : undefined
                    }
                  >
                    {creatingStripe ? "Creating..." : "Create Stripe Account"}
                  </button>
                  {hasStripeAccount && (
                    <button
                      type="button"
                      className="btn btn-primary rounded-lg px-3"
                      onClick={handleRefreshStripeAccount}
                      disabled={refreshingStripe || loadingStripe}
                    >
                      {refreshingStripe || loadingStripe ? "Refreshing..." : "Refresh"}
                    </button>
                  )}
                </div>
              </div>

              {loadingStripe ? (
                <div className="text-center py-4">
                  <div className="spinner-border" role="status">
                    <span className="sr-only">{UI_CONSTANTS.BUTTON_LABELS.LOADING}</span>
                  </div>
                </div>
              ) : hasStripeAccount ? (
                <div className="row">
                  <div className="col-md-4 mb-3">
                    <div className="border rounded-14 p-3 h-100">
                      <small className="text-muted d-block mb-1">Stripe Account ID</small>
                      <strong className="text-white">
                        {stripeAccount.stripe_account_id || "-"}
                      </strong>
                    </div>
                  </div>
                  <div className="col-md-4 mb-3">
                    <div className="border rounded-14 p-3 h-100">
                      <small className="text-muted d-block mb-1">Onboarded</small>
                      {getYesNoBadge(stripeAccount.stripe_onboarded)}
                    </div>
                  </div>
                  <div className="col-md-4 mb-3">
                    <div className="border rounded-14 p-3 h-100">
                      <small className="text-muted d-block mb-1">Payout Enabled</small>
                      {getYesNoBadge(stripeAccount.payout_enabled)}
                    </div>
                  </div>
                  <div className="col-md-4 mb-3">
                    <div className="border rounded-14 p-3 h-100">
                      <small className="text-muted d-block mb-1">Status</small>
                      <strong className="text-white">
                        {Number(stripeAccount.status) === 1 ? "Active" : "Inactive"}
                      </strong>
                    </div>
                  </div>
                  <div className="col-md-4 mb-3">
                    <div className="border rounded-14 p-3 h-100">
                      <small className="text-muted d-block mb-1">Created</small>
                      <strong className="text-white">
                        {formatDateTime(stripeAccount.createdAt)}
                      </strong>
                    </div>
                  </div>
                  <div className="col-md-4 mb-3">
                    <div className="border rounded-14 p-3 h-100">
                      <small className="text-muted d-block mb-1">Updated</small>
                      <strong className="text-white">
                        {formatDateTime(stripeAccount.updatedAt)}
                      </strong>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-2">
                  <p className="text-muted mb-0">
                    No Stripe account found. Create one to enable payouts.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        show={refreshModal.open}
        onClose={handleCloseRefreshModal}
        style={{ zIndex: "1000" }}
        className="shadow-none"
      >
        <div className="modal-dialog modal-md modal-center" role="document">
          <div className="modal-content bg-black border-light">
            <div className="modal-body my-3 py-4 px-4">
              <h4 className="text-white text-center mb-3">
                {refreshModal.title || "Stripe Refresh Result"}
              </h4>
              {refreshModal.message ? (
                <p className="text-muted text-center mb-4">{refreshModal.message}</p>
              ) : null}

              {refreshOnboardingLink ? (
                <div className="row">
                  <div className="col-12 mb-3">
                    <small className="text-muted d-block mb-1">Object</small>
                    <strong className="text-white">
                      {refreshOnboardingLink.object || "-"}
                    </strong>
                  </div>
                  <div className="col-md-6 mb-3">
                    <small className="text-muted d-block mb-1">Created</small>
                    <strong className="text-white">
                      {formatUnixDateTime(refreshOnboardingLink.created)}
                    </strong>
                  </div>
                  <div className="col-md-6 mb-3">
                    <small className="text-muted d-block mb-1">Expires At</small>
                    <strong className="text-white">
                      {formatUnixDateTime(refreshOnboardingLink.expires_at)}
                    </strong>
                  </div>
                  <div className="col-12 mb-3">
                    <small className="text-muted d-block mb-1">Onboarding URL</small>
                    {refreshOnboardingLink.url ? (
                      <a
                        href={refreshOnboardingLink.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary text-break d-inline-block"
                      >
                        {refreshOnboardingLink.url}
                      </a>
                    ) : (
                      <strong className="text-white">-</strong>
                    )}
                  </div>
                </div>
              ) : refreshAccountDetail ? (
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <small className="text-muted d-block mb-1">Stripe Account ID</small>
                    <strong className="text-white">
                      {refreshAccountDetail.stripe_account_id || "-"}
                    </strong>
                  </div>
                  <div className="col-md-6 mb-3">
                    <small className="text-muted d-block mb-1">Onboarded</small>
                    {getYesNoBadge(refreshAccountDetail.stripe_onboarded)}
                  </div>
                  <div className="col-md-6 mb-3">
                    <small className="text-muted d-block mb-1">Payout Enabled</small>
                    {getYesNoBadge(refreshAccountDetail.payout_enabled)}
                  </div>
                  <div className="col-md-6 mb-3">
                    <small className="text-muted d-block mb-1">Status</small>
                    <strong className="text-white">
                      {Number(refreshAccountDetail.status) === 1 ? "Active" : "Inactive"}
                    </strong>
                  </div>
                </div>
              ) : (
                <p className="text-muted text-center mb-0">No additional details returned.</p>
              )}

              <div className="text-center mt-3">
                <button
                  type="button"
                  className="btn btn-primary btn-lg px-5 rounded-pill"
                  onClick={handleCloseRefreshModal}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        show={successModal.open}
        onClose={() => {
          if (!confirmingSuccess) {
            setSuccessModal({ open: false, accountDetailId: null });
          }
        }}
        style={{ zIndex: "1000" }}
        className="shadow-none"
      >
        <div className="modal-dialog modal-md modal-center" role="document">
          <div className="modal-content bg-black border-light">
            <div className="modal-body text-center my-4 py-4">
              <h4 className="text-white mb-3">Stripe Account Created</h4>
              <p className="text-muted mb-4">
                Your Stripe account has been created successfully.
              </p>
              <button
                type="button"
                className="btn btn-primary btn-lg px-5 rounded-pill"
                onClick={handleSuccessOk}
                disabled={confirmingSuccess}
              >
                {confirmingSuccess ? "Please wait..." : "OK"}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default AdminProfile;
